import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, List, ListItemButton, ListItemIcon, ListSubheader, Divider,
  ListItemText, IconButton, Avatar, Menu, MenuItem, Chip, Alert, Button, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon, AutoAwesome, Science, Link as LinkIcon,
  Description, Forum, CreditCard, Menu as MenuIcon, Logout, Hub, ViewKanban, Apps,
  Tune, AccountTree, ShoppingCart, SupportAgent, Instagram, AdminPanelSettings, Business, Quiz, CalendarMonth,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { api } from '../config/api';
import Logo from '../components/Logo';
import MgeekFooter from '../components/MgeekFooter';
import AyudaChatbot from '../components/AyudaChatbot';
import BotonAyudaModulo from '../components/AyudaModulo';
import SelectorAgente from '../components/SelectorAgente';

const drawerWidth = 260;

interface NavItem { label: string; path: string; icon: React.ReactNode }

/** Menú agrupado por tareas, para que cada módulo sea fácil de encontrar. */
const GRUPOS: { titulo: string; items: NavItem[] }[] = [
  { titulo: '', items: [{ label: 'Dashboard', path: '/app', icon: <DashboardIcon /> }] },
  { titulo: 'Tu agente', items: [
    { label: 'Mi Agente', path: '/app/agente', icon: <AutoAwesome /> },
    { label: 'Playground', path: '/app/playground', icon: <Science /> },
    { label: 'Orquestación', path: '/app/orquestacion', icon: <Tune /> },
    { label: 'Flujos', path: '/app/flujos', icon: <AccountTree /> },
  ] },
  { titulo: 'Canales', items: [
    { label: 'Conexión WhatsApp', path: '/app/conexion', icon: <LinkIcon /> },
    { label: 'Instagram y Messenger', path: '/app/redes-sociales', icon: <Instagram /> },
    { label: 'Plantillas WhatsApp', path: '/app/plantillas', icon: <Description /> },
  ] },
  { titulo: 'Clientes', items: [
    { label: 'Conversaciones', path: '/app/conversaciones', icon: <Forum /> },
    { label: 'Agenda', path: '/app/agenda', icon: <CalendarMonth /> },
    { label: 'Pipeline', path: '/app/pipeline', icon: <ViewKanban /> },
    { label: 'Soporte y widget', path: '/app/soporte', icon: <SupportAgent /> },
  ] },
  { titulo: 'Conexiones', items: [
    { label: 'Integraciones', path: '/app/integraciones', icon: <Hub /> },
    { label: 'WooCommerce', path: '/app/woocommerce', icon: <ShoppingCart /> },
  ] },
  { titulo: 'Cuenta', items: [
    { label: 'Mi perfil', path: '/app/perfil', icon: <Business /> },
    { label: 'Suscripción', path: '/app/suscripcion', icon: <CreditCard /> },
    { label: 'Preguntas frecuentes', path: '/app/faq', icon: <Quiz /> },
    { label: 'Otros proyectos de mGeek', path: '/app/otros-proyectos', icon: <Apps /> },
  ] },
];

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, suscripcion, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [empresa, setEmpresa] = useState<string>('');

  // Logo y nombre de la empresa (Mi perfil) para la barra superior.
  useEffect(() => {
    api.get('/perfil').then((r) => { setLogo(r.data.empresa?.logo_data_url || null); setEmpresa(r.data.empresa?.nombre_fantasia || ''); }).catch(() => {});
    const h = (e: Event) => { const d = (e as CustomEvent).detail || {}; if ('logo' in d) setLogo(d.logo || null); };
    window.addEventListener('respondo:perfil-actualizado', h);
    return () => window.removeEventListener('respondo:perfil-actualizado', h);
  }, []);

  // El grupo de Admin solo aparece para administradores.
  const grupos = user?.rol === 'admin'
    ? [{ titulo: '', items: [{ label: 'Admin', path: '/app/admin', icon: <AdminPanelSettings /> }] }, ...GRUPOS]
    : GRUPOS;

  const activo = (path: string) => (path === '/app' ? location.pathname === '/app' : location.pathname === path || location.pathname.startsWith(path + '/'));

  const drawer = (
    <Box>
      <Toolbar>
        <Logo size={32} />
      </Toolbar>
      <List dense sx={{ pt: 0 }}>
        {grupos.map((g, gi) => (
          <React.Fragment key={g.titulo || `g${gi}`}>
            {g.titulo && (
              <ListSubheader disableSticky sx={{ lineHeight: '30px', mt: 1, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'transparent' }}>
                {g.titulo}
              </ListSubheader>
            )}
            {g.items.map((item) => {
              const act = activo(item.path);
              return (
                <ListItemButton
                  key={item.path}
                  selected={act}
                  onClick={() => { navigate(item.path); setMobileOpen(false); }}
                  sx={{
                    mx: 1, borderRadius: 2, mb: 0.3, py: 0.8,
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '& .MuiListItemIcon-root': { color: 'white' }, '&:hover': { bgcolor: 'primary.dark' } },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: act ? 'white' : 'text.secondary' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
                </ListItemButton>
              );
            })}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  // El admin tiene acceso ilimitado: nunca ve avisos de licencia.
  const esAdmin = user?.rol === 'admin';
  const sinLicencia = !esAdmin && !!suscripcion && (suscripcion.vigente === false || ['vencida', 'cancelada'].includes(suscripcion.estado));
  const enGracia = !esAdmin && suscripcion?.estado === 'gracia';
  const chip = !suscripcion ? null
    : esAdmin ? { color: 'success' as const, label: 'Admin' }
    : suscripcion.estado === 'activa' ? { color: 'success' as const, label: suscripcion.planNombre || 'Plan activo' }
    : suscripcion.estado === 'gracia' ? { color: 'warning' as const, label: `Pago pendiente: ${suscripcion.diasRestantes} días` }
    : suscripcion.estado === 'trial' && !sinLicencia ? { color: 'warning' as const, label: `Prueba: ${suscripcion.diasRestantes} días` }
    : { color: 'error' as const, label: suscripcion.estado === 'cancelada' ? 'Plan cancelado' : suscripcion.estado === 'trial' ? 'Prueba finalizada' : 'Plan vencido' };
  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" color="inherit" elevation={0} sx={{ zIndex: (t) => t.zIndex.drawer + 1, borderBottom: '1px solid #E2E8F0' }}>
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <SelectorAgente />
          <Box sx={{ flexGrow: 1 }} />
          {chip && (
            <Chip size="small" color={chip.color} label={chip.label} sx={{ mr: 2, cursor: 'pointer' }} onClick={() => navigate('/app/suscripcion')} />
          )}
          <BotonAyudaModulo />
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Mi cuenta">
            <Avatar src={logo || undefined} variant={logo ? 'rounded' : 'circular'} sx={{ bgcolor: 'primary.main', width: 34, height: 34 }}>
              {(empresa || user?.nombre || '?')[0]?.toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <MenuItem disabled sx={{ flexDirection: 'column', alignItems: 'flex-start', opacity: '1 !important' }}>
              <Box sx={{ fontWeight: 700 }}>{empresa || user?.nombre}</Box>
              <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{user?.email}</Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchor(null); navigate('/app/perfil'); }}><Business fontSize="small" sx={{ mr: 1 }} /> Mi perfil</MenuItem>
            <MenuItem onClick={() => { setAnchor(null); navigate('/app/suscripcion'); }}><CreditCard fontSize="small" sx={{ mr: 1 }} /> Suscripción</MenuItem>
            <MenuItem onClick={() => { setAnchor(null); navigate('/app/faq'); }}><Quiz fontSize="small" sx={{ mr: 1 }} /> Preguntas frecuentes</MenuItem>
            <Divider />
            <MenuItem onClick={logout}><Logout fontSize="small" sx={{ mr: 1 }} /> Cerrar sesión</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', borderRight: '1px solid #E2E8F0' } }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` }, minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1 }}>
          {(sinLicencia || enGracia) && location.pathname !== '/app/suscripcion' && (
            <Alert
              severity={sinLicencia ? 'error' : 'warning'}
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => navigate('/app/suscripcion')}>{sinLicencia ? 'Reactivar' : 'Pagar ahora'}</Button>}
            >
              {sinLicencia
                ? (suscripcion?.estado === 'trial' ? 'Tu prueba gratis terminó.' : 'Tu plan no está vigente.') + ' Tu agente no está respondiendo a tus clientes.'
                : `Tu periodo pagado terminó. El agente sigue respondiendo ${suscripcion?.diasRestantes} día(s) más; paga para no interrumpir la atención.`}
            </Alert>
          )}
          <Outlet />
        </Box>
        <MgeekFooter variant="light" />
      </Box>

      {/* Asistente de ayuda con IA — flotante en toda la app */}
      <AyudaChatbot />
    </Box>
  );
};

export default AppLayout;
